
import React, { useState, useEffect, useMemo } from "react";
import { Info, User, Search } from "lucide-react";
import { format } from "date-fns";
import { useSettings } from "@/providers/SettingsProvider";
import { getFormattedDateTime } from "@/utils/settings";
import { supabase } from "@/integrations/supabase/client";
import { HistoryItem } from "@/hooks/useHistoryOperations";
import { Input } from "@/components/ui/input";

interface EntityHistoryProps {
	history: HistoryItem[];
	loading: boolean;
}

// Helper function to check if a string is a UUID
const isUUID = (str: string): boolean => {
	const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
	return uuidRegex.test(str);
};

// Component to resolve and display user names
const UserDisplayName = ({ performedBy }: { performedBy: string | null }) => {
	const [userName, setUserName] = useState<string>(performedBy || 'System');
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const resolveUserName = async () => {
			if (!performedBy) {
				setUserName('System');
				return;
			}

			// If it's a UUID, resolve it to a user name
			if (isUUID(performedBy)) {
				setLoading(true);
				try {
					const { data, error } = await supabase
						.rpc('get_user_display_info', {
							p_user_id: performedBy
						});
					
					if (!error && data && data.length > 0) {
						const userInfo = data[0];
						const fullName = `${userInfo.first_name || ''} ${userInfo.last_name || ''}`.trim();
						setUserName(fullName || 'Unknown User');
					} else {
						setUserName(`User ${performedBy.substring(0, 8)}`);
					}
				} catch (error) {
					console.warn("Could not fetch user info:", error);
					setUserName(`User ${performedBy.substring(0, 8)}`);
				} finally {
					setLoading(false);
				}
			} else {
				// Already a readable name, use as is
				setUserName(performedBy);
			}
		};

		resolveUserName();
	}, [performedBy]);

	if (loading) {
		return <span>Loading...</span>;
	}

	return <span>{userName}</span>;
};

// Format field name to be more readable
const formatFieldName = (field: string): string => {
	return field
		.split('_')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
};

// Format relations field to show what changed
const formatRelations = (oldValue: any, newValue: any): { display: string, added: string[], removed: string[] } => {
	const oldRel = oldValue || {};
	const newRel = newValue || {};
	
	const added: string[] = [];
	const removed: string[] = [];
	
	// Check each relation type
	for (const relType of ['candidates', 'companies', 'jobs', 'matches', 'tasks', 'events']) {
		const oldIds = Array.isArray(oldRel[relType]) ? oldRel[relType] : [];
		const newIds = Array.isArray(newRel[relType]) ? newRel[relType] : [];
		
		// Find added items
		const addedIds = newIds.filter((id: string) => !oldIds.includes(id));
		if (addedIds.length > 0) {
			added.push(`${addedIds.length} ${relType}`);
		}
		
		// Find removed items
		const removedIds = oldIds.filter((id: string) => !newIds.includes(id));
		if (removedIds.length > 0) {
			removed.push(`${removedIds.length} ${relType}`);
		}
	}
	
	const parts = [];
	if (added.length > 0) parts.push(`Added: ${added.join(', ')}`);
	if (removed.length > 0) parts.push(`Removed: ${removed.join(', ')}`);
	
	return {
		display: parts.length > 0 ? parts.join(' | ') : 'No changes',
		added,
		removed
	};
};

// JSON fields to exclude from display (but still tracked in database)
const JSON_FIELDS_TO_HIDE = [
	'ai_analysis',
	'ai_science_analysis',
	'custom_fields',
	'linkedin_json',
	'scorecard_data',
	'metadata',
	'query_definition',
	'embedding',
	'embedding_specific',
	'experience_centroid',
	'skill_centroid'
	// Note: 'relations' removed - now handled with custom rendering
];

// Fields that need special formatting
const SPECIAL_FIELDS = ['relations'];

export const EntityHistory = ({ history, loading }: EntityHistoryProps) => {
	const { dateFormat, timeFormat } = useSettings();
	const [searchTerm, setSearchTerm] = useState("");

	// Parse legacy description format for backward compatibility
	const parseChangedValues = (description: string) => {
		const match = description.match(/^(.+?):\s*Changed from\s*"([^"]*)"\s*to\s*"([^"]*)"$/);
		if (match) {
			return {
				field: match[1].trim(),
				oldValue: match[2],
				newValue: match[3]
			};
		}
		return null;
	};

	// Filter history based on search term
	const filteredHistory = useMemo(() => {
		if (!searchTerm.trim()) return history;
		
		const searchLower = searchTerm.toLowerCase();
		return history.filter(item => {
			// Check if any changed field matches the search
			if (item.changed_fields) {
				return item.changed_fields.some(field => 
					formatFieldName(field).toLowerCase().includes(searchLower)
				);
			}
			// For legacy items, check the description
			if (item.description) {
				return item.description.toLowerCase().includes(searchLower);
			}
			return false;
		});
	}, [history, searchTerm]);

	if (loading) {
		return (
			<div className="space-y-4">
				{[1, 2, 3].map((i) => (
					<div key={i} className="h-20 w-full animate-pulse bg-gray-100 dark:bg-gray-800 rounded-sm" />
				))}
			</div>
		);
	}

	if (!history.length) {
		return (
			<div className="text-center py-8 border dark:border-gray-700 rounded-sm">
				<Info className="h-8 w-8 text-gray-400 dark:text-gray-500 mx-auto" />
				<p className="mt-2 text-gray-500 dark:text-gray-400">No history available</p>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{/* Search input */}
			<div className="relative">
				<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
				<Input
					type="text"
					placeholder="Search by field name..."
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					className="pl-9"
				/>
			</div>

			{filteredHistory.length === 0 ? (
				<div className="text-center py-8 border dark:border-gray-700 rounded-sm">
					<Info className="h-8 w-8 text-gray-400 dark:text-gray-500 mx-auto" />
					<p className="mt-2 text-gray-500 dark:text-gray-400">No matching history found</p>
				</div>
			) : (
				<div className="border dark:border-gray-700 rounded-sm overflow-hidden">
					<table className="w-full text-sm">
						<thead className="bg-gray-50 dark:bg-gray-800 border-b dark:border-gray-700">
							<tr>
								<th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Operation</th>
								<th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Date & Time</th>
								<th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">User</th>
								<th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Field</th>
								<th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Old Value</th>
								<th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">New Value</th>
							</tr>
						</thead>
						<tbody>
							{filteredHistory.map((item) => {
								const legacyChange = item.description ? parseChangedValues(item.description) : null;
								const hasNewStructure = item.changed_fields && item.changed_fields.length > 0;
								const hasLegacyStructure = legacyChange !== null;
								const visibleFields = hasNewStructure 
									? item.changed_fields.filter(field => !JSON_FIELDS_TO_HIDE.includes(field))
									: [];

								// Skip items with no details to show
								if (!hasNewStructure && !hasLegacyStructure && 
									item.operation !== 'INSERT' && item.operation !== 'DELETE') {
									return null;
								}

								// For INSERT/DELETE with no visible fields, show single row
								if ((item.operation === 'INSERT' || item.operation === 'DELETE') && visibleFields.length === 0 && !hasLegacyStructure) {
									return (
										<tr key={item.id} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
											<td className="px-4 py-3">
												<span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
													item.operation === 'INSERT' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
												}`}>
													{item.operation}
												</span>
											</td>
											<td className="px-4 py-3 text-gray-600 dark:text-gray-400">
												{getFormattedDateTime(new Date(item.changed_at), dateFormat, timeFormat)}
											</td>
											<td className="px-4 py-3 text-gray-600 dark:text-gray-400">
												<UserDisplayName performedBy={item.changed_by || item.performed_by} />
											</td>
											<td colSpan={3} className="px-4 py-3 text-gray-500 dark:text-gray-400 italic">
												{item.operation === 'INSERT' ? 'Record created' : 'Record deleted'}
											</td>
										</tr>
									);
								}

								// For legacy structure
								if (hasLegacyStructure) {
									return (
										<tr key={item.id} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
											<td className="px-4 py-3">
												<span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-500 dark:bg-blue-900/30 dark:text-blue-400">
													UPDATE
												</span>
											</td>
											<td className="px-4 py-3 text-gray-600 dark:text-gray-400">
												{getFormattedDateTime(new Date(item.changed_at), dateFormat, timeFormat)}
											</td>
											<td className="px-4 py-3 text-gray-600 dark:text-gray-400">
												<UserDisplayName performedBy={item.changed_by || item.performed_by} />
											</td>
											<td className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">{legacyChange.field}</td>
											<td className="px-4 py-3 text-gray-600 dark:text-gray-400 max-w-xs truncate" title={legacyChange.oldValue}>
												{legacyChange.oldValue || "—"}
											</td>
											<td className="px-4 py-3 text-gray-900 dark:text-gray-100 font-medium max-w-xs truncate" title={legacyChange.newValue}>
												{legacyChange.newValue || "—"}
											</td>
										</tr>
									);
								}

								// For new structure with multiple fields
								return visibleFields.map((field, idx) => {
									const oldVal = item.old_value?.[field];
									const newVal = item.new_value?.[field];
									
									// Special handling for relations
									if (field === 'relations') {
										const relationChanges = formatRelations(oldVal, newVal);
										
										return (
											<tr key={`${item.id}-${field}`} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
												{idx === 0 ? (
													<>
														<td className="px-4 py-3" rowSpan={visibleFields.length}>
															<span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
																item.operation === 'INSERT' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
																item.operation === 'UPDATE' ? 'bg-blue-100 text-blue-500 dark:bg-blue-900/30 dark:text-blue-400' :
																'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
															}`}>
																{item.operation}
															</span>
														</td>
														<td className="px-4 py-3 text-gray-600 dark:text-gray-400" rowSpan={visibleFields.length}>
															{getFormattedDateTime(new Date(item.changed_at), dateFormat, timeFormat)}
														</td>
														<td className="px-4 py-3 text-gray-600 dark:text-gray-400" rowSpan={visibleFields.length}>
															<UserDisplayName performedBy={item.changed_by || item.performed_by} />
														</td>
													</>
												) : null}
												<td className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">{formatFieldName(field)}</td>
												<td colSpan={2} className="px-4 py-3 text-gray-900 dark:text-gray-100">
													{relationChanges.display}
												</td>
											</tr>
										);
									}
									
									const displayOld = oldVal !== null && oldVal !== undefined ? String(oldVal) : '—';
									const displayNew = newVal !== null && newVal !== undefined ? String(newVal) : '—';

									return (
										<tr key={`${item.id}-${field}`} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
											{idx === 0 ? (
												<>
													<td className="px-4 py-3" rowSpan={visibleFields.length}>
														<span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
															item.operation === 'INSERT' ? 'bg-green-100 text-green-700' :
															item.operation === 'UPDATE' ? 'bg-blue-100 text-blue-500 dark:bg-blue-900/30 dark:text-blue-400' :
															'bg-red-100 text-red-700'
														}`}>
															{item.operation}
														</span>
													</td>
													<td className="px-4 py-3 text-gray-600 dark:text-gray-400" rowSpan={visibleFields.length}>
														{getFormattedDateTime(new Date(item.changed_at), dateFormat, timeFormat)}
													</td>
													<td className="px-4 py-3 text-gray-600 dark:text-gray-400" rowSpan={visibleFields.length}>
														<UserDisplayName performedBy={item.changed_by || item.performed_by} />
													</td>
												</>
											) : null}
											<td className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">{formatFieldName(field)}</td>
											<td className="px-4 py-3 text-gray-600 dark:text-gray-400 max-w-xs truncate" title={displayOld}>
												{displayOld}
											</td>
											<td className="px-4 py-3 text-gray-900 dark:text-gray-100 font-medium max-w-xs truncate" title={displayNew}>
												{displayNew}
											</td>
										</tr>
									);
								});
							})}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};